<template>
  <form-base>
    <template v-slot:form>
      <v-form ref="form" class="d-flex create-appointment-form pt-0 inline-fields">
        <v-stepper v-model="step" class="full-width" flat>
          <v-stepper-header>
            <v-stepper-step :complete="step > 1" step="1"> Warehouse </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :complete="step > 2" step="2"> Load Type </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :complete="step > 3" step="3">
              <div class="is-relative">
                <span>Dock</span>
                <div class="font-size-x-small stepper-subtitle" v-if="displayDockDisclaimer">
                  Automatically Selected
                </div>
              </div>
            </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :complete="step > 4" step="4"> Date & Time </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step step="5"> Appointment Details </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content step="2" class="px-0">
              <!-- No docks to select -->
              <template v-if="selectedWarehouse?.docks.length === 0">
                <v-alert color="warning lighten-2" icon="mdi-alert" dense class="my-8">
                  <h4>There are no docks available for scheduling at this warehouse.</h4>
                </v-alert>
              </template>
              <template v-else>
                <div class="step-heading">Select Load Type</div>
                <load-type-select-table
                  :selected-warehouse="$warehouse"
                  v-model="selectedLoadType"
                  @selected="proceed">
                </load-type-select-table>
              </template>
            </v-stepper-content>

            <v-stepper-content step="3" class="px-0">
              <div class="step-heading">Select Dock</div>
              <template v-if="canSelectDock">
                <dock-select-table
                  :docks="docksToSelect"
                  v-model="selectedDock"
                  @selected="proceed">
                </dock-select-table>
              </template>
              <!-- No docks to select -->
              <template v-else-if="docksToSelect.length === 0">
                <v-alert color="warning lighten-2" icon="mdi-alert" dense class="my-8">
                  <h4>
                    There are no docks available for scheduling this load type, please select
                    another load type.
                  </h4>
                </v-alert>
              </template>
            </v-stepper-content>

            <v-stepper-content step="4" class="px-0">
              <template>
                <div class="step-heading">Select Date & Time</div>
                <date-time-picker
                  v-if="step === 4"
                  v-model="selectedTime"
                  :numDays="5"
                  :selected-warehouse="$warehouse"
                  :selected-load-type="selectedLoadType ?? docksToSelect"
                  :selected-docks="docksForDateTimePicker"
                  :start-date="selectedDate"
                  :timezone="$warehouse.timezone"
                  @availability-error="handleAvailabilityError">
                </date-time-picker>
              </template>
            </v-stepper-content>

            <v-stepper-content step="5" class="px-0">
              <div class="step-heading">Appointment Details</div>
              <v-container class="appointment-details-container">
                <v-row v-if="$refNumSettings($warehouse).isVisible">
                  <h3>
                    {{ $refNumSettings($warehouse).displayName }}
                    <help-icon-tooltip v-if="$refNumSettings($warehouse).helperText">
                      {{ $refNumSettings($warehouse).helperText }} </help-icon-tooltip
                    >:
                  </h3>
                  <auto-expandable-textarea
                    v-model="refNumber"
                    :label="refNumberPlaceholder"
                    :placeholder="refNumberPlaceholder"
                    validate-on-blur
                    :rules="$validator.rules.required($refNumSettings($warehouse).displayName)"
                    required
                    v-if="$refNumSettings($warehouse).isRequired"></auto-expandable-textarea>
                  <auto-expandable-textarea
                    v-else
                    v-model="refNumber"
                    :label="refNumberPlaceholder"
                    :placeholder="refNumberPlaceholder"
                    validate-on-blur>
                  </auto-expandable-textarea>
                </v-row>
                <custom-fields
                  v-if="selectedWarehouse.id"
                  v-model="customFields"
                  :warehouse="selectedWarehouse"
                  @toggle-confirm-button="toggleConfirmButton">
                </custom-fields>
                <v-row class="mt-4">
                  <h3>Appointment Notes:</h3>
                  <rich-text-input
                    class="flex-fill"
                    :editor-config="{
                      plugins: ['Essentials', 'Paragraph'],
                      toolbar: { items: [] }
                    }"
                    v-model="notes"></rich-text-input>
                </v-row>
                <v-row class="mt-4">
                  <h3>
                    Email Subscribers
                    <help-icon-tooltip>
                      List of email addresses that will receive all notifications about the
                      appointment.
                    </help-icon-tooltip>
                    :
                  </h3>
                  <email-list-field
                    small-chips
                    small
                    v-model="ccEmails"
                    ref="emailFieldList"></email-list-field>
                </v-row>
              </v-container>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-form>
    </template>
    <template v-slot:form-actions>
      <action-group
        :loading="loading"
        :hide-cancel="true"
        :confirm-label="stepperLabelNext"
        :hide-confirm="!showNextButton"
        :disable-confirm="isConfirmDisabled"
        :confirm-button-tooltip="confirmButtonTooltip"
        @confirm="proceed">
        <template v-slot:additional-actions>
          <outline-button @click="previous" v-if="step > 2" before-icon="arrow-left">
            Previous
          </outline-button>
        </template>
      </action-group>
    </template>
  </form-base>
</template>

<script>
/**
 * Form that creates an appointment
 * @displayName Create Appointment Form
 */
export default {
  props: {
    /**
     * Calendar context when dayview is clicked
     * contains date, time, warehouse, and dock
     */
    context: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    }
  },
  computed: {
    displayDockDisclaimer() {
      return (
        this.step > 2 &&
        this.selectedLoadType?.id &&
        (!this.canSelectDock || this.docksToSelect.length < 2) &&
        this.docksToSelect.length > 0
      );
    },
    docksForDateTimePicker() {
      return this.selectedDock?.id ? [this.selectedDock] : null;
    },
    canSelectDock() {
      return this.selectedLoadType?.settings?.allowCarrierDockSelection ?? false;
    },
    docksToSelect() {
      return this.selectedWarehouse?.docks.filter(dock => {
        const loadTypeInDock =
          !this.selectedLoadType?.id || dock.loadTypeIds.includes(this.selectedLoadType.id);

        const isNotChild = !this.novaCore.isCapacityChild(dock);

        const allowScheduling = dock.allowCarrierScheduling === true;
        return allowScheduling && isNotChild && loadTypeInDock;
      });
    },
    isConfirmDisabled() {
      return this.disableConfirm;
    },
    confirmButtonTooltip() {
      return this.$isMrPreview ? 'Preview Mode only, you cannot finalize an appointment.' : '';
    },
    displayTimePicker() {
      return this.selectedLoadType?.id;
    },
    /**
     * Label for the next button depending on step
     * @returns {string}
     */
    stepperLabelNext() {
      return this.step < this.stepsTotal
        ? 'Next'
        : `${
            this.novaCore.isRequested(this.$appointmentCreationStatus) ? 'Request' : 'Confirm'
          } Appointment`;
    },
    /**
     * If a warehouse has been selected
     * @returns {boolean}
     */
    warehouseSelected() {
      return Boolean(this.selectedWarehouse?.id);
    },
    /**
     * Display the next button in the stepper
     * @returns {*}
     */
    showNextButton() {
      let showNextButton = false;
      if (this.step === 2) {
        showNextButton = this.isStep2Valid();
      } else if (this.step === 3) {
        showNextButton = this.isStep3Valid();
      } else if (this.step === 4) {
        showNextButton = this.isStep4Valid();
      } else {
        showNextButton = true;
      }

      return showNextButton;
    },
    selectedLoadType: {
      get() {
        return this.$bookingData.selectedLoadType;
      },
      set(loadType) {
        this.$store.commit('Appointments/setBookingData', {
          key: 'selectedLoadType',
          data: loadType
        });
      }
    },
    selectedDock: {
      get() {
        return this.$bookingData.selectedDock;
      },
      set(dock) {
        this.$store.commit('Appointments/setBookingData', {
          key: 'selectedDock',
          data: dock
        });
      }
    },
    selectedTime: {
      get() {
        return this.$bookingData.selectedSlot;
      },
      set(slot) {
        this.$store.commit('Appointments/setBookingData', { key: 'selectedSlot', data: slot });
      }
    },
    refNumberPlaceholder() {
      return (
        this.$refNumSettings(this.$warehouse).helperText ||
        this.$refNumSettings(this.$warehouse).displayName
      );
    },
    getSelectedDock() {
      return this.selectedDock.id || _.first(this.selectedTime?.docks);
    }
  },
  data() {
    return {
      loading: false,
      step: 2,
      stepsTotal: 5,
      submitted: false,
      selectedOrg: {},
      selectedWarehouse: {},
      selectedCarrier: null,
      selectedDate: '',
      customFields: [],
      refNumber: '',
      ccEmails: [],
      disableConfirm: false,
      mounted: false,
      notes: ''
    };
  },
  methods: {
    isStep2Valid() {
      return Boolean(this.$bookingData.selectedLoadType.id);
    },
    isStep3Valid() {
      return Boolean(this.$bookingData.selectedDock.id);
    },
    isStep4Valid() {
      return Boolean(this.$bookingData.selectedSlot.start) && Boolean(this.getSelectedDock);
    },
    /**
     * Submit the form
     * @public
     * @returns {Promise<void>}
     */
    async submit() {
      if (
        this.$refs.form.validate() &&
        this.$refs.emailFieldList.validate() &&
        Boolean(this.getSelectedDock)
      ) {
        await this.createAppointment();
      }
    },
    /**
     * Create appointment
     * @public
     * @returns {Promise<void>}
     */
    async createAppointment() {
      this.loading = true;
      const shouldSuppressNotification = this.novaCore.isRequested(this.$appointmentCreationStatus);
      try {
        const requestConfig = {
          suppressNotification: shouldSuppressNotification
        };
        const response = await this.$store.dispatch('Appointments/createAppointment', {
          userId: this.selectedCarrier.id,
          warehouseId: this.selectedWarehouse.id,
          loadTypeId: this.selectedLoadType.id,
          dockId: this.getSelectedDock,
          start: this.selectedTime.start.clone().format(),
          refNumber: this.refNumber,
          notes: this.notes,
          ccEmails: this.ccEmails,
          customFields: this.customFields,
          requestConfig
        });

        if (response?.data) {
          if (this.novaCore.isRequested(this.$appointmentCreationStatus)) {
            this.notify('Appointment Requested');
          }
          if (this.$warehouseId && this.$warehouseOrgId) {
            this.mixpanel.track(this.mixpanel.events.ACTION.APPOINTMENT_CREATED, {
              'Warehouse ID': this.$warehouseId,
              'Org ID': this.$warehouseOrgId,
              'Appointment ID': response.data.id,
              'Appointment Start': response.data.start,
              'Appointment Ref#': response.data.refNumber,
              'Created By': 'Carrier user',
              'Creation Status': response?.data?.status
            });
          }

          await this.$router.replace({
            path: `/warehouse/${this.$warehouseId}/appointments/${response.data.id}`
          });
        }
      } catch (err) {
        if (shouldSuppressNotification) {
          const message = err.response?.data?.message ?? err.message;
          this.notify(message, 'error');
        }
      } finally {
        this.loading = false;
      }
    },
    /**
     * Emit close event when form lives in dialog
     * @public
     */
    cancel() {
      /**
       * Emits close event
       * @public
       * @event close
       */
      this.$emit('close');
    },
    /**
     * proceed to next step
     * @public
     */
    proceed() {
      this.$nextTick(() => {
        if (this.step < this.stepsTotal) {
          if (
            this.step === 2 &&
            (!this.canSelectDock || (this.canSelectDock && this.docksToSelect.length < 2))
          ) {
            this.step++;
          }
          this.step++;

          this.scrollToHeader();
        } else {
          this.submit();
        }
      });
    },
    previous() {
      if (this.step === 4 && (this.docksToSelect.length < 2 || !this.canSelectDock)) {
        this.step--;
      }
      this.step--;
    },
    /**
     * Clears all form data
     * @public
     */
    clearData() {
      const dataToClear = [
        { key: 'selectedLoadType', data: {} },
        { key: 'selectedDock', data: {} },
        { key: 'selectedSlot', data: {} },
        { key: 'start', data: '' },
        { key: 'refNumber', data: null },
        { key: 'ccEmails', data: [] },
        { key: 'customFields', data: [] }
      ];
      dataToClear.forEach(data => {
        this.$store.commit('Appointments/setBookingData', data);
      });
    },
    toggleConfirmButton(disableButton) {
      this.disableConfirm = disableButton;
    },
    autoIncrementStep() {
      this.step++;
    },
    scrollToHeader() {
      const titleElements = document.getElementsByClassName('v-stepper__header');
      if (titleElements?.length > 0) {
        titleElements[0].scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    },
    async setOrgCarrierSettings() {
      const shouldGetOrgCarrierSettings =
        this.selectedWarehouse?.orgId &&
        this.selectedCarrier?.id &&
        this.novaCore.isCarrierUser(this.$me);

      if (shouldGetOrgCarrierSettings) {
        this.selectedCarrier.orgCarrierSettings = await this.$store.dispatch(
          'Warehouses/getOrgCarrierSettings',
          this.selectedWarehouse.orgId
        );
        if (this.selectedCarrier.orgCarrierSettings?.emailCCs?.length > 0) {
          this.ccEmails.push(...this.selectedCarrier.orgCarrierSettings.emailCCs);
        }
      }
    },
    async handleAvailabilityError() {
      /**
       * Back to step 2 after 750ms
       * The intention of the delay is just a UX improvement to avoid a fast transition between steps
       */
      setTimeout(() => {
        if (this.isStep2Valid() && this.step === 3) {
          this.step = 2;
        }
      }, 750);
    }
  },
  async created() {
    this.selectedWarehouse = this.context.selectedWarehouse || {};
    this.selectedDock = this.context.selectedDock || {};
    this.selectedCarrier = this.context.selectedCarrier || {};
    this.selectedDate = this.context.selectedDate || '';
    this.selectedTime = this.context.selectedTime || {};
  },
  mounted() {
    this.mounted = true;
    this.setOrgCarrierSettings();
    this.clearData();
  },
  watch: {
    '$bookingData.selectedLoadType'(value) {
      if (value?.id) {
        this.selectedDock = {};
        this.selectedTime = {};
        if (this.docksToSelect.length === 1) {
          this.selectedDock = this.docksToSelect[0];
        }
        if (this.docksToSelect.length < 2) {
          this.step++;
        }
      }
    },
    '$bookingData.selectedSlot'(value) {
      if (value.start) {
        this.autoIncrementStep(value);
      }
    }
  }
};
</script>
